'use client';

import React, { useMemo, useState } from 'react';
import { css } from 'styled-components';

import { ImageSlide } from '@/app/components/hotel-detail/HeroGalleryImageSlide';
import { getGallerySourceOptimization } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { HeroNoImageAvailable } from '@/app/components/hotel-detail/HeroNoImageAvailable';
import { getPhotoAlt } from '@/app/components/hotel-detail/getPhotoAlt';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';

import EvoGalleryModal from './EvoGalleryModal';
import EvoGalleryPhotoCountBadge from './EvoGalleryPhotoCountBadge';

type Props = {
  images: string[];
  name: string;
} & React.HTMLAttributes<HTMLElement>;

const MAX_INITIAL_IMAGES = 5;

export default function EvoHotelDetailsGallery({ images, name, ...rest }: Props) {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState<boolean | undefined>(undefined);
  const [initialIndex, setInitialIndex] = useState(0);

  const initialImages = images?.slice(0, MAX_INITIAL_IMAGES);
  const imageAmount = initialImages?.length ?? 0;

  const initialImagesWithSources = useMemo(
    () =>
      getGallerySourceOptimization({
        images: initialImages?.length ? initialImages : [''],
        isMobile: false,
        isTopSecret: false,
        totalImages: initialImages?.length || 0,
      }),
    [initialImages]
  );

  if (!images?.length) return <HeroNoImageAvailable />;

  const openAt = (index: number) => {
    setInitialIndex(index);
    setModalOpen(true);
  };

  return (
    <>
      <div
        css={css`
          position: relative;
        `}
        {...rest}
      >
        <div
          css={css`
            position: relative;
            height: 260px;
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            padding: 6px 0;
            box-sizing: content-box;

            &::-webkit-scrollbar {
              display: none;
            }

            @media ${customCss.breakpoints.sm} {
              display: grid;
              grid-gap: 8px;
              z-index: 0;
              padding: 6px;
              ${
                imageAmount === 5
                  ? `
                  grid-template-columns: 2fr 1fr 1fr;
                  grid-template-rows: 1fr 1fr;
                  & > *:first-child { grid-row: 1 / 3; }
                `
                  : imageAmount === 4
                    ? `
                  grid-template-columns: 2fr 1fr 1fr;
                  grid-template-rows: 1fr 1fr;
                  & > *:first-child, & > *:nth-child(2) { grid-row: 1 / 3; }
                `
                    : imageAmount === 3
                      ? `
                  grid-template-columns: 2fr 1fr;
                  grid-template-rows: 1fr 1fr;
                  & > *:first-child { grid-row: 1 / 3; }
                `
                      : imageAmount === 2
                        ? `grid-template-columns: 2fr 1fr;`
                        : ``
              }
              & > * {
                max-height: 100%;
                overflow: hidden;
              }
            }

            @media ${customCss.breakpoints.sm} {
              height: 300px;
            }

            /* lg, not xl: the drawer is a fixed 928px from 768px up and only mounts above lg,
               so keying this to xl left 1024-1279px on the 300px sm rule inside the same drawer. */
            @media ${customCss.breakpoints.lg} {
              height: 344px;
            }
          `}
        >
          {initialImagesWithSources.map((image, index) => {
            const isLastWithMore =
              index === initialImagesWithSources.length - 1 && images.length > initialImagesWithSources.length;
            return (
              <ImageSlide
                url={image.url}
                sources={image.sources}
                key={image.url}
                role="button"
                aria-label={getPhotoAlt(t, name, index, images.length)}
                tabIndex={0}
                loading="lazy"
                onClick={() => openAt(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openAt(index);
                  }
                }}
                overlay={
                  isLastWithMore ? (
                    <EvoGalleryPhotoCountBadge
                      imageAmount={images.length}
                      css={`
                        position: absolute;
                        bottom: 16px;
                        right: 16px;
                      `}
                    />
                  ) : undefined
                }
                css={`
                  flex: 0 0 100%;
                `}
              />
            );
          })}
        </div>
      </div>

      <EvoGalleryModal
        images={images}
        name={name}
        isOpen={modalOpen}
        initialImageIndex={initialIndex}
        modalId="evo-details-gallery-modal"
        onDismiss={() => setModalOpen(false)}
        onReopen={() => setModalOpen(true)}
      />
    </>
  );
}
